import MoreIcon from '@mui/icons-material/MoreHoriz'
import { IconButton } from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import axios from 'axios'
import config from 'config'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import logger from 'logger'
import T from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import DateCell from 'components/atoms/DateCell'
import DotCell from 'components/atoms/DotCell'
import FilterDateRange from 'components/atoms/FilterDateRange'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useEntityData from 'hooks/useEntityData'
import useQuery from 'hooks/useQuery'

import ConfirmationDialog from '../../atoms/ConfirmationDialog'
import useSnackbar from '../Snackbar/useSnackbar'

import PartnerInfoDialog from './PartnerInfoDialog'
import UserInfoDialog from './UserInfoDialog'
import UsersHeader from './UsersHeader'
import useUsersFilters from './useUsersFilters'
import {
  USERS_ACCOUNT_TYPE_ARVAL_USERS_LIST,
  USERS_ACCOUNT_TYPE_PARTNER_USERS_LIST,
  USERS_LIST_TYPE_ARVAL_USERS,
  USERS_LIST_TYPE_PARTNERS,
  USERS_LIST_TYPE_PARTNER_USERS,
} from './usersLib'

const StatusRenderer = (value, t) => {
  return (
    <DotCell
      color={value === 'enabled' ? 'primary' : 'error'}
      label={t(`users.status.${value}`)}
    />
  )
}

const pathToAccountTypeFilter = {
  arvalUsers: USERS_ACCOUNT_TYPE_ARVAL_USERS_LIST,
  partnerUsers: USERS_ACCOUNT_TYPE_PARTNER_USERS_LIST,
}

const COMMON_FORM_SEARCH = 'common.form.search'
const UsersContent = ({
  enabledListTypes,
  defaultType,
  contentIsReadOnly = false,
}) => {
  const { t } = useTranslation()
  const query = useQuery()
  const listType = query.get('type') || defaultType

  const { showSnackbarMessage } = useSnackbar()
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedUser, setSelectedUser] = useState()
  const [selectedPartner, setSelectedPartner] = useState()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newRowsPerPage, setNewRowsPerPage] = useState(10)
  const [newPage, setNewPage] = useState(0)

  const {
    initialized,
    filters,
    filtersData,
    setEditorQuery,
    setNameQuery,
    setSurnameQuery,
    setEmailQuery,
    setCreatedAtRange,
    setUpdatedAtRange,
    setPartners,
    setAccount,
    setStates,
    setSort,
    initFilters,
    setGenericFilter,
  } = useUsersFilters(listType)

  const onMenuClick = useCallback(
    (event, user) => {
      setAnchorEl(event.currentTarget)

      if (listType === USERS_LIST_TYPE_PARTNERS) {
        setSelectedPartner(user)
      } else {
        setSelectedUser(user)
      }
    },
    [listType]
  )

  const handleChangePage = (_, newPage) => {
    setNewPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setNewRowsPerPage(parseInt(event.target.value, 10))
    setNewPage(0)
  }

  const handleResetMFA = useCallback(async () => {
    try {
      await axios.post(
        `${config.apiBaseUrl}users/reset-mfa/${selectedUser.userId}`,
        {}
      )

      showSnackbarMessage({
        severity: 'success',
        message: t('users.mfaDialog.success'),
      })
    } catch (e) {
      logger.error('[Error] - partner creation', e)
      showSnackbarMessage({
        severity: 'error',
        message: t('users.mfaDialog.error'),
      })
    }
  }, [selectedUser, showSnackbarMessage, t])

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleResetMFA)

  const actionCell = useCallback(
    (props) => {
      if (!!contentIsReadOnly) {
        return <></>
      } else {
        return (
          <IconButton
            aria-label="More"
            onClick={(e) => onMenuClick(e, props.row.original)}
            data-testid={`action-${props.row.id}`}
          >
            <MoreIcon />
          </IconButton>
        )
      }
    },
    [onMenuClick, contentIsReadOnly]
  )

  const usersColumns = useMemo(() => {
    if (
      !filters?.initialized ||
      ![USERS_LIST_TYPE_ARVAL_USERS, USERS_LIST_TYPE_PARTNER_USERS].includes(
        listType
      )
    )
      return []

    const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)

    return [
      ...(listType === USERS_LIST_TYPE_PARTNER_USERS
        ? [
            {
              accessor: 'partner.name',
              Header: (
                <TableHeaderCell
                  label={t('users.tableColumns.partner')}
                  filter={
                    <FilterSelect
                      allOption={allOption}
                      options={filtersData.partners}
                      value={filters.partners}
                      handleChange={setPartners}
                      multiple
                      hasSearchField
                      data-testid="filter-partner"
                    />
                  }
                />
              ),
            },
          ]
        : []),
      {
        accessor: 'firstName',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.name')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.nameQuery}
                onChange={(event) => setNameQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-nameQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'lastName',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.surname')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.surnameQuery}
                onChange={(event) => setSurnameQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-surnameQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'email',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.email')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.emailQuery}
                onChange={(event) => setEmailQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-emailQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'createdAt',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.createdAt')}
            filter={
              <FilterDateRange
                value={[filters.createdAtFrom, filters.createdAtTo]}
                handleChange={setCreatedAtRange}
                data-testid="filter-createdAt"
              />
            }
          />
        ),
        Cell: DateCell,
      },
      {
        accessor: 'updatedAt',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.lastEdit')}
            filter={
              <FilterDateRange
                value={[filters.updatedAtFrom, filters.updatedAtTo]}
                handleChange={setUpdatedAtRange}
                data-testid="filter-updatedAt"
              />
            }
          />
        ),
        Cell: DateCell,
      },
      {
        accessor: 'lastEditorEmail',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.editor')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.editorQuery}
                onChange={(event) => setEditorQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-editorQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'accountType',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.accountType')}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.account}
                value={filters.account}
                handleChange={setAccount}
                multiple
                data-testid="filter-account"
              />
            }
          />
        ),
      },
      {
        accessor: 'state',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.status')}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.states}
                value={filters.states}
                handleChange={setStates}
                data-testid="filter-state"
              />
            }
          />
        ),
        Cell: ({ value }) => StatusRenderer(value, t),
      },
      {
        id: 'options',
        Cell: actionCell,
      },
    ]
  }, [
    filters,
    filtersData,
    listType,
    setAccount,
    setCreatedAtRange,
    setEditorQuery,
    setEmailQuery,
    setNameQuery,
    setPartners,
    setStates,
    setSurnameQuery,
    setUpdatedAtRange,
    t,
    actionCell,
  ])

  const partnersColumns = useMemo(() => {
    if (!filters?.initialized || ![USERS_LIST_TYPE_PARTNERS].includes(listType))
      return []

    const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)

    return [
      {
        accessor: 'partnerGroup',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partnerGroup')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.partnerGroupQuery}
                onChange={(event) =>
                  setGenericFilter('partnerGroupQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
        sticky: 'left',
      },
      {
        accessor: 'name',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partnerName')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.partnerNameQuery}
                onChange={(event) =>
                  setGenericFilter('partnerNameQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
        cellProps: {
          sx: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '200px',
          },
        },
        sticky: 'left',
      },
      {
        accessor: 'partnerId',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partnerId')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.partnerIdQuery}
                onChange={(event) =>
                  setGenericFilter('partnerIdQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
      },
      {
        accessor: 'partnerIdB2b',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partnerIdB2b')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.partnerIdB2bQuery}
                onChange={(event) =>
                  setGenericFilter('partnerIdB2bQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
        cellProps: {
          sx: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '200px',
          },
        },
        Cell: ({ value }) =>
          (value || '')
            ?.split(',')
            .map((id) => id.trim())
            .join(', '),
      },
      {
        accessor: 'vat',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.vat')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.vatQuery}
                onChange={(event) =>
                  setGenericFilter('vatQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
      },
      {
        accessor: 'createdAt',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.createdAt')}
            filter={
              <FilterDateRange
                value={[filters.createdAtFrom, filters.createdAtTo]}
                handleChange={setCreatedAtRange}
              />
            }
          />
        ),
        Cell: DateCell,
      },
      {
        accessor: 'updatedAt',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.lastEdit')}
            filter={
              <FilterDateRange
                value={[filters.updatedAtFrom, filters.updatedAtTo]}
                handleChange={setUpdatedAtRange}
              />
            }
          />
        ),
        Cell: DateCell,
      },
      {
        accessor: 'lastEditorEmail',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.editor')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.editorQuery}
                onChange={(event) =>
                  setGenericFilter('editorQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
      },
      {
        accessor: 'state',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.status')}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.states}
                value={filters.states}
                handleChange={setStates}
              />
            }
          />
        ),
        Cell: ({ value }) => StatusRenderer(value, t),
      },
      {
        accessor: 'notes',
        Cell: ({ value }) => String(value || '').substring(0, 20),
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.notes')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.notesQuery}
                onChange={(event) =>
                  setGenericFilter('notesQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
      },
      {
        id: 'options',
        Cell: actionCell,
      },
    ]
  }, [
    t,
    filters,
    filtersData,
    setCreatedAtRange,
    setGenericFilter,
    setStates,
    setUpdatedAtRange,
    listType,
    actionCell,
  ])

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedUser()
  }

  const onEditClick = () => {
    setDialogOpen(true)
  }

  const prepareFilters = useCallback(
    (filters = {}, filtersData = {}) => {
      return {
        ...filters,
        accountType: pathToAccountTypeFilter[listType],
      }
    },
    [listType]
  )

  useEffect(() => {
    setNewPage(0)
  }, [listType, filters])

  const {
    items,
    isFetching,
    total,
    isFetchingOtherPages,
    page,
    rowsPerPage,
    refetch,
  } = useEntityData({
    newPage,
    limit: newRowsPerPage,
    filters: JSON.stringify(prepareFilters(filters, filtersData)),
    sort: filters?.order?.column,
    direction: filters?.order?.direction,
  })

  const handleFilters = useDebouncedCallback(async () => {
    refetch(listType === USERS_LIST_TYPE_PARTNERS ? 'partners' : 'users')
  }, 100)

  useEffect(() => {
    if (!initialized) return
    handleFilters()
  }, [refetch, newPage, newRowsPerPage, handleFilters, initialized])

  useEffect(() => {
    if (initialized) {
      return
    }
    initFilters(listType)
  }, [items, initFilters, initialized, listType])

  const columns =
    listType === USERS_LIST_TYPE_PARTNERS ? partnersColumns : usersColumns
  return (
    <>
      <UsersHeader
        onNew={() => {
          if (contentIsReadOnly === false) {
            setSelectedUser(null)
            setSelectedPartner(null)
            setDialogOpen(true)
          }
        }}
        listType={listType}
        enabledListTypes={enabledListTypes}
        contentIsReadOnly={contentIsReadOnly}
      />

      {initialized && !!columns.length && (
        <Table
          items={items}
          columns={columns}
          isFetching={isFetching}
          isFetchingOtherPages={isFetchingOtherPages}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          hasPagination={true}
          rowsPerPageOptions={[10, 20, 30, 50]}
          sortable
          excludedSortableIds={['options']}
          orderColumn={filters?.order?.column}
          orderDirection={filters?.order?.direction}
          handleSort={setSort}
        />
      )}

      {contentIsReadOnly === false && (
        <Menu
          data-testid={`action-menu`}
          id="options-menu"
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleCloseMenu}
        >
          <MenuItem
            data-testid={`action-menu-edit`}
            onClick={() => {
              if (contentIsReadOnly === false) {
                onEditClick()
                handleCloseMenu()
              }
            }}
          >
            {t('users.menu.edit')}
          </MenuItem>
          {listType !== USERS_LIST_TYPE_PARTNERS && (
            <MenuItem
              data-testid={`action-menu-reset-mfa`}
              onClick={() => {
                if (contentIsReadOnly === false) {
                  setSelectedUser(selectedUser)
                  handleOpenConfirmationDialog()
                  handleCloseMenu()
                }
              }}
            >
              Reset MFA
            </MenuItem>
          )}
        </Menu>
      )}

      {listType === USERS_LIST_TYPE_PARTNERS ? (
        <PartnerInfoDialog
          open={dialogOpen}
          partner={selectedPartner}
          handleClose={handleCloseDialog}
          refetch={refetch.bind(null, 'partners')}
        />
      ) : (
        <UserInfoDialog
          open={dialogOpen}
          user={selectedUser}
          handleClose={handleCloseDialog}
          refetch={refetch.bind(null, 'users')}
          allowedRoles={pathToAccountTypeFilter[listType]}
        />
      )}
      <ConfirmationDialog
        title={t('users.mfaDialog.title')}
        content={<>{t('users.mfaDialog.content')}</>}
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </>
  )
}

export default UsersContent

UsersContent.propTypes = {
  enabledListTypes: T.arrayOf(T.string),
  selectedSessionId: T.string,
  contentIsReadOnly: T.bool,
}
